import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useUiState, getUiState, setUiState } from "@/lib/uiState";
import { MapPin, ChevronDown, ChevronUp, Maximize2, Minimize2, Layers, BoxSelect, X, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CENTROIDS = {
  AL: [32.8, -86.8], AK: [64.2, -149.5], AZ: [34.3, -111.7], AR: [34.9, -92.4], CA: [37.2, -119.5],
  CO: [39.0, -105.5], CT: [41.6, -72.7], DE: [39.0, -75.5], FL: [28.6, -82.4], GA: [32.6, -83.4],
  HI: [20.3, -156.4], ID: [44.3, -114.6], IL: [40.0, -89.2], IN: [39.9, -86.3], IA: [42.0, -93.5],
  KS: [38.5, -98.4], KY: [37.5, -85.3], LA: [31.0, -92.0], ME: [45.4, -69.2], MD: [39.0, -76.8],
  MA: [42.3, -71.8], MI: [44.3, -85.4], MN: [46.3, -94.3], MS: [32.7, -89.7], MO: [38.4, -92.5],
  MT: [46.9, -110.0], NE: [41.5, -99.8], NV: [39.4, -116.9], NH: [43.7, -71.6], NJ: [40.2, -74.7],
  NM: [34.4, -106.1], NY: [42.9, -75.5], NC: [35.5, -79.4], ND: [47.5, -100.5], OH: [40.3, -82.8],
  OK: [35.6, -97.5], OR: [44.0, -120.5], PA: [40.9, -77.6], RI: [41.7, -71.5], SC: [33.9, -80.9],
  SD: [44.4, -100.2], TN: [35.9, -86.4], TX: [31.5, -99.3], UT: [39.3, -111.7], VT: [44.1, -72.7],
  VA: [37.5, -78.9], WA: [47.4, -120.5], WV: [38.6, -80.6], WI: [44.6, -89.9], WY: [43.0, -107.6], DC: [38.9, -77.0], PR: [18.2, -66.4],
};

// Full state name (for matching GeoJSON `properties.name`) keyed by 2-letter abbrev.
const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia", PR: "Puerto Rico",
};
const NAME_TO_ABBR = Object.fromEntries(Object.entries(STATE_NAMES).map(([a, n]) => [n.toUpperCase(), a]));

// Distinct colors so overlapping chapter regions stay readable.
const CHAPTER_COLORS = ["#001A4B", "#7A1F2B", "#1E5631", "#5B2A86", "#B5651D", "#0F6E7B"];

// Derive all 2-letter state abbrevs from a chapter's location string, which may list
// multiple "City, ST" pairs (e.g. "Boston, MA, Augusta ME") to span several states.
function statesFromLocation(loc) {
  if (!loc) return [];
  const found = [];
  const add = (ab) => { if (ab && !found.includes(ab)) found.push(ab); };
  const parts = String(loc).split(",").map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    const up = p.toUpperCase();
    if (STATE_NAMES[up]) { add(up); continue; }
    if (NAME_TO_ABBR[up]) { add(NAME_TO_ABBR[up]); continue; }
    const words = up.split(/\s+/).filter(Boolean);
    const last = words[words.length - 1];
    if (STATE_NAMES[last]) { add(last); continue; }
    const last2 = words.slice(-2).join(" ");
    if (NAME_TO_ABBR[last2]) add(NAME_TO_ABBR[last2]);
  }
  return found;
}

const US_STATES_GEOJSON = "https://cdn.jsdelivr.net/gh/PublicaMundi/MappingAPI@master/data/geojson/us-states.json";

const MAP_OPTS = { scrollWheelZoom: true, zoomSnap: 0.25, zoomDelta: 0.5, wheelPxPerZoomLevel: 120, zoomAnimation: false, markerZoomAnimation: false, fadeAnimation: false };

// Persist/restore the map's center + zoom so the chosen view is remembered.
const DEFAULT_VIEW = { center: [39.5, -98.35], zoom: 4 };
function loadView() {
  // Always re-center on the middle of the U.S.; keep only the user's last zoom level.
  let zoom = DEFAULT_VIEW.zoom;
  const v = getUiState("ud_map_view", null);
  if (v && typeof v.zoom === "number") zoom = v.zoom;
  return { center: DEFAULT_VIEW.center, zoom };
}
function saveView(map) {
  try {
    const c = map.getCenter();
    setUiState("ud_map_view", { center: [c.lat, c.lng], zoom: map.getZoom() });
  } catch { /* noop */ }
}

export default function MapPanel({ alumni = [], title = "Alumni Map", hiddenNote = "", canRefresh = false, classYear = null, onGeocodeDone = null }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const fsRef = useRef(null);
  const fsMapRef = useRef(null);
  const selectRectRef = useRef(null);
  const [ready, setReady] = useState(!!window.L);
  const [open, setOpen] = useUiState("ud_map_open", true);
  const [full, setFull] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selResults, setSelResults] = useState(null);
  const [mapHeight, setMapHeight] = useUiState("ud_map_height", 340);

  // Keep Leaflet sized to the container when the inline height changes.
  useEffect(() => {
    if (mapRef.current) { try { mapRef.current.invalidateSize(); } catch { /* noop */ } }
  }, [mapHeight]);

  // Drag the bottom handle to resize the inline map vertically.
  const startResize = (e) => {
    e.preventDefault();
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const startH = mapHeight;
    const onMove = (ev) => {
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setMapHeight(Math.min(Math.max(startH + (y - startY), 240), 900));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  // Load Leaflet from CDN once.
  useEffect(() => {
    if (window.L) { setReady(true); return; }
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => setReady(true);
    document.body.appendChild(s);
  }, []);

  const [chapters, setChapters] = useState([]);
  const [statesGeo, setStatesGeo] = useState(null);
  const [showChapters, setShowChapters] = useUiState("ud_map_show_chapters", true);
  const [zoom, setZoom] = useState(DEFAULT_VIEW.zoom);

  const [geo, setGeo] = useState({ active: false, done: 0, total: 0 });
  const geoWasActive = useRef(false);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/geocode/status");
        if (!alive) return;
        setGeo(data);
        if (data.active) geoWasActive.current = true;
        else if (geoWasActive.current) { geoWasActive.current = false; onGeocodeDone && onGeocodeDone(); }
      } catch (_) { /* ignore (e.g. non-staff) */ }
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [onGeocodeDone]);

  const refreshCoords = async () => {
    try {
      const { data } = await api.post("/geocode/refresh", classYear ? { class_year: classYear } : {});
      if (data.queued > 0) {
        toast.info(`Mapping ${data.queued} address${data.queued === 1 ? "" : "es"} in the background…`);
        setGeo({ active: true, done: 0, total: data.queued });
        geoWasActive.current = true;
      } else {
        toast.success("All addresses are already mapped.");
      }
    } catch (_) {
      toast.error("Could not start map refresh");
    }
  };
  useEffect(() => {
    api.get("/groups").then((r) => setChapters(
      (r.data.groups || [])
        .filter((g) => g.type === "chapter")
        .map((g) => ({ ...g, stateAbbrs: (g.states && g.states.length ? g.states : statesFromLocation(g.location)).map((s) => String(s).toUpperCase()) }))
        .filter((g) => g.stateAbbrs.length)
    )).catch(() => {});
  }, []);

  // Load US state boundaries once (for chapter region outlines).
  useEffect(() => {
    fetch(US_STATES_GEOJSON).then((r) => r.json()).then(setStatesGeo).catch(() => {});
  }, []);

  // Plot alumni markers + chapter markers onto a given map; returns the layer group.
  const plot = (L, map) => {
    const group = L.layerGroup();
    displayPoints.forEach(({ a, lat, lng, precise }) => {
      const marker = L.circleMarker([lat, lng], {
        radius: precise ? 7 : 5,
        color: precise ? "#C8A24C" : "#94a3b8",
        weight: 1,
        fillColor: precise ? "#001A4B" : "#64748b",
        fillOpacity: precise ? 0.9 : 0.55,
      });
      marker.bindTooltip(
        `<b>${a.name || ""}</b><br>${[a.city, a.state].filter(Boolean).join(", ")}${a.class_year ? `<br>Class of ${a.class_year}` : ""}${precise ? "" : "<br><i>approximate (state)</i>"}<br><span style="color:#C8A24C;font-weight:600">Click to open profile →</span>`,
        { direction: "top", offset: [0, -4], opacity: 1 }
      );
      if (a.id) {
        marker.on("click", () => { setFull(false); navigate(`/alumni/${a.id}`); });
      }
      marker.addTo(group);
    });
    // Draw each chapter as a shaded region: its home state + all contiguous states.
    if (statesGeo && showChapters) {
      const byName = {};
      statesGeo.features.forEach((f) => { byName[(f.properties.name || "").toUpperCase()] = f; });
      chapters.forEach((c, ci) => {
        const color = CHAPTER_COLORS[ci % CHAPTER_COLORS.length];
        const feats = c.stateAbbrs
          .map((ab) => byName[(STATE_NAMES[ab] || "").toUpperCase()])
          .filter(Boolean);
        const stateNames = c.stateAbbrs.map((ab) => STATE_NAMES[ab] || ab).join(", ");
        if (feats.length) {
          L.geoJSON(
            { type: "FeatureCollection", features: feats },
            { interactive: false, style: () => ({ color, weight: 2.5, fillColor: color, fillOpacity: 0.28, dashArray: null }) }
          ).addTo(group);
        }
        // One label per chapter, centered on the average of its states' centroids.
        const centroids = c.stateAbbrs.map((ab) => CENTROIDS[ab]).filter(Boolean);
        let ctr = null;
        if (typeof c.lat === "number" && typeof c.lng === "number") {
          ctr = [c.lat, c.lng];
        } else if (centroids.length) {
          ctr = [
            centroids.reduce((s, p) => s + p[0], 0) / centroids.length,
            centroids.reduce((s, p) => s + p[1], 0) / centroids.length,
          ];
        }
        if (ctr) {
          L.marker(ctr, {
            icon: L.divIcon({
              className: "",
              html: `<div style="white-space:nowrap;background:${color};color:#fff;border:1.5px solid #C8A24C;border-radius:9999px;padding:2px 8px;font-size:11px;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,0.3);transform:translate(-50%,-50%)">${c.name}</div>`,
              iconSize: [0, 0], iconAnchor: [0, 0],
            }),
          }).bindPopup(`<b>${c.name}</b><br><span style="color:#C8A24C;text-transform:uppercase;font-size:10px">Chapter</span><br>Serves ${stateNames}`).addTo(group);
        }
      });
    }
    group.addTo(map);
    return group;
  };

  // Final display coordinates for every alumnus. Precise points sharing the SAME location
  // (e.g. several classmates geocoded to the same city) are fanned out on a small ring so
  // each shows as a distinct dot — and the box-selection count matches what you see.
  // Computed once and reused by both plot() and box-selection so they always agree.
  const displayPoints = useMemo(() => {
    const base = alumni.map((a, i) => {
      if (typeof a.lat === "number" && typeof a.lng === "number") return { a, lat: a.lat, lng: a.lng, precise: true };
      const c = CENTROIDS[(a.state || "").toUpperCase().trim()];
      if (!c) return null;
      const seed = (a.id || String(i)).split("").reduce((h, ch) => h + ch.charCodeAt(0), 0);
      return { a, lat: c[0] + ((seed % 20) / 20 - 0.5) * 1.4, lng: c[1] + (((seed * 7) % 20) / 20 - 0.5) * 1.4, precise: false };
    }).filter(Boolean);
    const groups = {};
    base.forEach((p) => { if (p.precise) { const k = `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`; (groups[k] = groups[k] || []).push(p); } });
    const GOLDEN = Math.PI * (3 - Math.sqrt(5)); // ~137.5° — sunflower/phyllotaxis angle
    Object.values(groups).forEach((g) => {
      if (g.length < 2) return;
      const n = g.length;
      const Rmax = Math.min(0.05, 0.01 + 0.007 * Math.sqrt(n));
      const cosLat = Math.max(0.2, Math.cos(g[0].lat * Math.PI / 180));
      g.forEach((p, idx) => {
        const r = Rmax * Math.sqrt((idx + 0.5) / n); // fills a disk (not a ring)
        const ang = idx * GOLDEN;
        p.lat = p.lat + r * Math.cos(ang);
        p.lng = p.lng + (r * Math.sin(ang)) / cosLat;
      });
    });
    return base;
  }, [alumni]);

  const points = displayPoints;

  const clearSelection = () => {
    const map = full ? fsMapRef.current : mapRef.current;
    if (map && selectRectRef.current) { try { map.removeLayer(selectRectRef.current); } catch { /* noop */ } }
    selectRectRef.current = null;
    setSelResults(null);
  };

  // Box-select mode: drag a rectangle on the map to list the alumni inside it (with class years).
  useEffect(() => {
    if (!selecting) return;
    const map = full ? fsMapRef.current : mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    if (selectRectRef.current) { try { map.removeLayer(selectRectRef.current); } catch { /* noop */ } selectRectRef.current = null; }
    map.dragging.disable();
    if (map.boxZoom) map.boxZoom.disable();
    const container = map.getContainer();
    const prevCursor = container.style.cursor;
    container.style.cursor = "crosshair";
    let start = null, rect = null;
    const onDown = (e) => {
      start = e.latlng;
      rect = L.rectangle(L.latLngBounds(start, start), { color: "#C8A24C", weight: 2, fillColor: "#C8A24C", fillOpacity: 0.12, dashArray: "6,4", interactive: false }).addTo(map);
    };
    const onMove = (e) => { if (start && rect) rect.setBounds(L.latLngBounds(start, e.latlng)); };
    const onUp = (e) => {
      if (!start || !rect) return;
      const bounds = L.latLngBounds(start, e.latlng);
      const inside = points.filter((p) => bounds.contains([p.lat, p.lng])).map((p) => p.a);
      selectRectRef.current = rect;
      setSelResults(inside);
      map.off("mousedown", onDown); map.off("mousemove", onMove); map.off("mouseup", onUp);
      map.dragging.enable(); if (map.boxZoom) map.boxZoom.enable();
      container.style.cursor = prevCursor;
      setSelecting(false);
    };
    map.on("mousedown", onDown);
    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    return () => {
      map.off("mousedown", onDown); map.off("mousemove", onMove); map.off("mouseup", onUp);
      try { map.dragging.enable(); if (map.boxZoom) map.boxZoom.enable(); container.style.cursor = prevCursor; } catch { /* noop */ }
    };
  }, [selecting, full, points]);

  // Inline map (exists whenever expanded and not fullscreen).
  useEffect(() => {
    if (!ready || !open || full || !containerRef.current) return;
    const L = window.L;
    const v = loadView();
    const map = L.map(containerRef.current, MAP_OPTS).setView(v.center, v.zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 12 }).addTo(map);
    map.on("moveend zoomend", () => { saveView(map); setZoom(map.getZoom()); });
    setZoom(map.getZoom());
    mapRef.current = map;
    let group;
    const t = setTimeout(() => { try { map.invalidateSize(); group = plot(L, map); } catch { /* noop */ } }, 200);
    return () => { clearTimeout(t); try { if (group) map.removeLayer(group); map.remove(); } catch { /* noop */ } mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, open, full, alumni, chapters, statesGeo, showChapters]);

  // Fullscreen map (separate instance in a body portal — no node reparenting).
  useEffect(() => {
    if (!full || !ready) return;
    const L = window.L;
    let map, group, t;
    const start = setTimeout(() => {
      if (!fsRef.current) return;
      const v = loadView();
      map = L.map(fsRef.current, MAP_OPTS).setView(v.center, v.zoom);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 12 }).addTo(map);
      map.on("moveend zoomend", () => saveView(map));
      fsMapRef.current = map;
      t = setTimeout(() => { try { map.invalidateSize(); group = plot(L, map); } catch { /* noop */ } }, 80);
    }, 0);
    return () => { clearTimeout(start); clearTimeout(t); try { if (map) map.remove(); } catch { /* noop */ } fsMapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, ready, alumni, chapters, statesGeo, showChapters]);

  // Body scroll lock + Esc to exit while fullscreen.
  useEffect(() => {
    if (!full) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [full]);

  const withLoc = alumni.filter((a) => (typeof a.lat === "number" && typeof a.lng === "number") || CENTROIDS[(a.state || "").toUpperCase().trim()]).length;
  const precise = alumni.filter((a) => typeof a.lat === "number" && typeof a.lng === "number").length;

  const SelResults = ({ floating }) => (
    <div
      data-testid="map-selection-results"
      className={floating
        ? "absolute top-4 right-4 z-[1100] w-[420px] max-w-[38vw] max-h-[82vh] bg-card border border-border rounded-lg shadow-2xl flex flex-col"
        : "border-t border-border bg-muted/20"}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <div className="text-sm font-medium" data-testid="map-selection-count">{selResults.length} alumni in selection</div>
        <button data-testid="map-selection-clear" onClick={clearSelection} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title="Clear selection">
          <X className="h-4 w-4" />
        </button>
      </div>
      {selResults.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground">No alumni fall inside the box. Try drawing a larger area.</div>
      ) : (
        <div className={`overflow-auto divide-y divide-border ${floating ? "flex-1" : "max-h-64"}`}>
          {[...selResults]
            .sort((a, b) => (b.class_year ?? 0) - (a.class_year ?? 0) || (a.name || "").localeCompare(b.name || ""))
            .map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`map-sel-row-${a.id}`}
                onClick={() => { setFull(false); navigate(`/alumni/${a.id}`); }}
                className="w-full flex items-center justify-between gap-3 px-4 py-2 text-sm text-left hover:bg-primary/5 transition-colors group"
              >
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-foreground group-hover:text-primary group-hover:underline">{a.name}</span>
                  {[a.city, a.state].filter(Boolean).length ? <span className="text-xs text-muted-foreground"> · {[a.city, a.state].filter(Boolean).join(", ")}</span> : null}
                </span>
                <span className="shrink-0 flex items-center gap-2">
                  {typeof a.engagement?.score === "number" && <span data-testid={`map-sel-eng-${a.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary" title={`Engagement: ${a.engagement.tier}`}>{a.engagement.score}</span>}
                  <span className="text-xs font-medium text-primary">{a.class_year ? `Class of ${a.class_year}` : "—"}</span>
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );

  const HeaderRow = ({ isFull }) => (
    <div className="w-full flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
      <button data-testid="map-toggle" onClick={() => !isFull && setOpen((o) => !o)} className="flex items-center gap-2 font-serif text-lg tracking-tight hover:opacity-80">
        <MapPin className="h-4 w-4 text-primary" /> {title}
      </button>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {hiddenNote && <span data-testid="map-hidden-note" className="hidden sm:inline text-muted-foreground/80 italic">{hiddenNote}</span>}
        {geo.active
          ? <span data-testid="map-geocode-progress" className="flex items-center gap-1.5 text-primary font-medium"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Mapping {geo.done}/{geo.total}…</span>
          : <span>{precise} precise · {withLoc} located</span>}
        {canRefresh && (
          <button
            data-testid="map-refresh-geocode"
            onClick={refreshCoords}
            disabled={geo.active}
            title="Re-check addresses and map any that are missing coordinates"
            className="flex items-center gap-1.5 px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${geo.active ? "animate-spin" : ""}`} /> Refresh map
          </button>
        )}
        <button
          data-testid="map-select-area"
          onClick={() => { setOpen(true); setSelecting((v) => !v); }}
          title="Draw a box on the map to list the alumni inside it"
          className={`flex items-center gap-1.5 px-2 py-1 rounded border transition-colors ${selecting ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}
        >
          <BoxSelect className="h-3.5 w-3.5" /> {selecting ? "Draw a box…" : "Select area"}
        </button>
        <button
          data-testid="map-toggle-chapters"
          onClick={() => setShowChapters((v) => !v)}
          title={showChapters ? "Hide chapter regions" : "Show chapter regions"}          className={`flex items-center gap-1.5 px-2 py-1 rounded border transition-colors ${showChapters ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}
        >
          <Layers className="h-3.5 w-3.5" /> Chapters
        </button>
        <button
          data-testid="map-fullscreen"
          onClick={() => { setFull((f) => !f); setOpen(true); }}
          title={isFull ? "Exit full screen (Esc)" : "Full screen"}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
        >
          {isFull ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        {!isFull && (
          <button data-testid="map-collapse" onClick={() => setOpen((o) => !o)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden mb-4" data-testid="map-panel">
        <HeaderRow isFull={false} />
        {open && !full && (
          <div className="relative">
            <div ref={containerRef} style={{ height: `${mapHeight}px`, width: "100%" }} data-testid="map-canvas" />
            <input
              type="range"
              data-testid="map-zoom-slider"
              min={2}
              max={12}
              step={0.25}
              value={zoom}
              onChange={(e) => { const z = parseFloat(e.target.value); setZoom(z); if (mapRef.current) mapRef.current.setZoom(z); }}
              title={`Zoom: ${zoom.toFixed(2)}`}
              className="absolute left-3 bottom-9 z-[1000] w-40 h-2 cursor-pointer accent-[#001A4B] bg-white/85 rounded-full shadow py-1"
            />
            <div
              data-testid="map-resize-handle"
              onMouseDown={startResize}
              onTouchStart={startResize}
              title="Drag to resize the map"
              className="group flex items-center justify-center h-3 cursor-ns-resize bg-muted/40 hover:bg-muted border-t border-border select-none"
            >
              <div className="w-10 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/70" />
            </div>
            {ready && withLoc === 0 && <div className="px-4 py-2 text-xs text-muted-foreground">No alumni have a state on file for the current filter.</div>}
          </div>
        )}
        {full && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Map is open in full screen — press Esc or the exit button to return.</div>}
        {!full && selResults && <SelResults />}
      </div>

      {full && createPortal(
        <div className="fixed inset-0 z-[1000] bg-card flex flex-col" data-testid="map-fullscreen-overlay">
          <HeaderRow isFull />
          <div className="flex-1 min-h-0 relative">
            <div ref={fsRef} style={{ height: "100%", width: "100%" }} data-testid="map-canvas-full" />
            {selResults && <SelResults floating />}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
